/**
 * business.js — cliente de FlowexPay Business (nomina maker-checker).
 * Habla con el backend principal (/api/business/*, gated por whitelist piloto).
 */
import API from './api';

export async function myBusinesses() {
  const { data } = await API.get('/business/mine');
  return data.data || [];
}

export async function createBusiness(name, nit) {
  const { data } = await API.post('/business', { name, nit });
  return data.data;
}

/** (Dueno) agrega un operador (RRHH/contabilidad) por email. Prepara, nunca aprueba. */
export async function addOperator(businessId, email) {
  const { data } = await API.post(`/business/${businessId}/operators`, { email });
  return data.data;
}

export async function approveBeneficiaries(businessId, beneficiaries) {
  const { data } = await API.post(`/business/${businessId}/beneficiaries/approve`, { beneficiaries });
  return data.data;
}

export async function createBatch(businessId, label, rows) {
  const { data } = await API.post(`/business/${businessId}/payroll`, { label, rows });
  return data; // { data: batch, newBeneficiaries }
}

export async function requestApproval(businessId, batchId, phone) {
  const { data } = await API.post(`/business/${businessId}/payroll/${batchId}/request-approval`, { phone });
  return data; // { actionToken }
}

export async function approveBatch(businessId, batchId, actionToken, code) {
  const { data } = await API.post(`/business/${businessId}/payroll/${batchId}/approve`, { actionToken, code });
  return data.data;
}

export async function listBatches(businessId) {
  const { data } = await API.get(`/business/${businessId}/payroll`);
  return data.data || [];
}

export async function getBatch(businessId, batchId) {
  const { data } = await API.get(`/business/${businessId}/payroll/${batchId}`);
  return data.data;
}

// ---- Plantilla CSV (columnas contractuales del backend) ----
export const CSV_HEADER =
  'documento,tipo_doc,nombre,apellido,celular,destino,referencia,banco,tipo_cuenta,numero_cuenta,moneda,monto,concepto';

/**
 * La plantilla que descarga la empresa. Trae UN ejemplo de cada destino a
 * proposito: si blockchain no aparece en el archivo, nadie sabe que existe —
 * que fue justo lo que paso.
 *
 * EL BANCO TIENE COLUMNAS PROPIAS. Antes los tres datos que Payvalida exige
 * (banco, tipo de cuenta y numero) iban embutidos en `referencia` con formato
 * "banco:tipo:numero". La plantilla no lo preguntaba en ninguna parte, y la
 * primera prueba real trajo un correo en esa casilla. Si un dato hace falta,
 * la planilla lo pregunta con su nombre.
 *
 *   banco       -> columnas banco + tipo_cuenta + numero_cuenta (referencia vacia)
 *   flowexpay   -> `referencia` = correo    (el UNICO que exige cuenta creada)
 *   blockchain  -> `referencia` = direccion de Solana
 *
 * `documento` es obligatorio para banco: Payvalida dispersa contra la cedula
 * del titular, no contra el numero de cuenta solo.
 */
export function downloadTemplate() {
  const sample = [
    CSV_HEADER,
    '1023456789,CC,Maria,Gomez Ruiz,3001234567,banco,,BANCOLOMBIA,ahorros,12345678901,COP,3500000,Nomina quincena',
    '1098765432,CC,Pedro,Ruiz,3109876543,flowexpay,pedro@correo.com,,,,USD,900,Nomina quincena',
    'V-12345678,PS,Ana,Diaz,584121112233,blockchain,9Spodq6CmXasjYV4YvauEoNmPAoKryndvn6PjF2gWrtY,,,,USD,1200,Diseno julio',
  ].join('\n');
  const blob = new Blob([sample], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'plantilla_nomina_flowexpay.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Parsea el CSV de nomina -> rows para el backend. Devuelve { rows, errors }. */
export function parseCsv(text) {
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], errors: ['El archivo esta vacio'] };
  const errors = [];
  const start = lines[0].toLowerCase().startsWith('documento') ? 1 : 0;
  const rows = [];
  for (let i = start; i < lines.length; i++) {
    const p = lines[i].split(',').map((s) => s.trim());
    if (p.length < 7) { errors.push(`Linea ${i + 1}: faltan columnas (minimo 7)`); continue; }
    rows.push({
      docId: p[0], name: p[1], phone: p[2] || undefined,
      destType: (p[3] || '').toLowerCase(), destRef: p[4],
      currency: (p[5] || '').toUpperCase(), amount: Number(p[6]),
      concept: p[7] || undefined,
    });
  }
  return { rows, errors };
}

export const fmtMonto = (r) =>
  `${r.currency === 'COP' ? '$' + Math.round(r.amount).toLocaleString('es-CO') : '$' + Number(r.amount).toLocaleString('en-US')} ${r.currency}`;
